import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Sparkles, TrendingUp, AlertTriangle, CheckCircle2, RefreshCw, Lightbulb } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const toneStyles = {
  good: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warn: 'bg-amber-50 text-amber-700 border-amber-200',
  bad: 'bg-rose-50 text-rose-700 border-rose-200',
  info: 'bg-slate-50 text-slate-700 border-slate-200',
};

const priorityStyles = {
  high: 'bg-rose-100 text-rose-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-700',
};

const AIInsightsPanel = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/enterprise/ai/insights`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load AI insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const insights = data?.insights || {};
  const snap = data?.snapshot || {};
  const isFallback = insights._fallback;

  return (
    <Card className="card-panashe border-l-4 border-l-violet-500" data-testid="ai-insights-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="font-heading flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-violet-600" />
              AI Operations Intelligence
              {isFallback && (
                <Badge variant="outline" className="text-xs">Baseline</Badge>
              )}
            </CardTitle>
            {insights.headline && (
              <p className="text-sm text-slate-700 mt-1 font-medium" data-testid="ai-insights-headline">
                {insights.headline}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="ai-insights-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 p-3 bg-rose-50 text-rose-700 rounded-md text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading && !data && (
          <div className="space-y-2 animate-pulse">
            <div className="h-4 bg-slate-200 rounded w-3/4" />
            <div className="h-4 bg-slate-200 rounded w-1/2" />
            <div className="grid grid-cols-3 gap-2 mt-3">
              <div className="h-16 bg-slate-100 rounded" />
              <div className="h-16 bg-slate-100 rounded" />
              <div className="h-16 bg-slate-100 rounded" />
            </div>
          </div>
        )}

        {data && (
          <>
            {insights.summary && (
              <p className="text-sm text-slate-600 leading-relaxed" data-testid="ai-insights-summary">
                {insights.summary}
              </p>
            )}

            {Array.isArray(insights.highlights) && insights.highlights.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {insights.highlights.slice(0, 6).map((h, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-md border ${toneStyles[h.tone] || toneStyles.info}`}
                    data-testid={`ai-insight-highlight-${i}`}
                  >
                    <p className="text-xs uppercase tracking-wide opacity-70">{h.label}</p>
                    <p className="text-lg font-bold mt-0.5">{h.value}</p>
                  </div>
                ))}
              </div>
            )}

            {Array.isArray(insights.recommendations) && insights.recommendations.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1">
                  <Lightbulb className="h-3.5 w-3.5" />
                  Recommendations
                </p>
                <div className="space-y-2">
                  {insights.recommendations.slice(0, 4).map((rec, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-md"
                      data-testid={`ai-recommendation-${i}`}
                    >
                      <CheckCircle2 className="h-4 w-4 text-violet-600 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-800">{rec.title}</span>
                          {rec.priority && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${priorityStyles[rec.priority] || ''}`}>
                              {rec.priority}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5">{rec.action}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {insights.forecasts && (
              <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                  <div>
                    <p className="text-xs text-slate-500">7-day passengers</p>
                    <p className="text-sm font-bold" data-testid="ai-forecast-passengers">
                      {insights.forecasts.next_7_days_passengers ?? '—'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                  <div>
                    <p className="text-xs text-slate-500">7-day revenue</p>
                    <p className="text-sm font-bold" data-testid="ai-forecast-revenue">
                      ${insights.forecasts.next_7_days_revenue_usd ?? '—'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-slate-400 pt-1">
              Snapshot: {snap.bookings_today ?? 0} bookings today · {snap.trips_today ?? 0} trips · ${snap.revenue_today_usd ?? 0} USD
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AIInsightsPanel;
